import { faPause, faPlay, faStop } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { IoMdStopwatch } from "react-icons/io";
import { useNavigate } from "react-router-dom";

import * as devConfig from "../../app/configs/devConfig";
import { Pages } from "../../app/constants/routes";
import { generateRoutePath } from "../../app/constants/routes/utils";
import { getAuthToken } from "../../app/globalState/GlobalAuthState";
import { Button } from "../../components/newUI/button";
import { formatTime } from "../../constants/constants";
import useSignalR from "../../hooks/useSignalR";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import {
  type ITimer,
  increaseDuration,
  pauseTimer,
  setStoredTimerState,
  startTimer,
} from "../../services/TimerService";
import StartTimer from "../time/StartTimer";
import AddTimer from "./AddTimer";
import { getTimerTargetType } from "./timer/timerTarget";

const getTimerContactLabel = (timer: ITimer | null) =>
  timer?.customerName?.trim() || "No customer";

const Timer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { timer, duration } = useAppSelector((state) => state.timerReducer);

  const [isOpenAddTimer, setIsOpenAddTimer] = useState(false);
  const [isOpenStartTime, setIsOpenStartTime] = useState(false);

  const timerTargetType = getTimerTargetType(timer);
  const timerWorkLabel =
    timerTargetType === "workTask"
      ? timer?.workTask?.name || timer?.work?.name || "Subtask"
      : timer?.work?.name || "Work";
  const timerContactLabel = getTimerContactLabel(timer);

  useSignalR(
    `${devConfig.getServerBaseUrlForEnv()}/signalRHub`,
    getAuthToken().Authorization,
  );

  const closeAddTimer = () => {
    setIsOpenAddTimer(false);
  };

  const onClickPauseTimer = () => {
    if (timer?.timerStatus === "paused") {
      dispatch(
        startTimer({
          workId: timer?.workId,
          customerId: timer?.customerId,
          assignedUserId: timer?.assignedUserId,
          jobTaskId:
            getTimerTargetType(timer) === "workTask"
              ? timer?.jobTaskId
              : undefined,
          workTask:
            getTimerTargetType(timer) === "workTask"
              ? timer?.workTask
              : undefined,
          work: timer?.work,
          customerName: timer?.customerName,
        }),
      );
    } else {
      dispatch(pauseTimer(timer?.id || ""));
    }
  };

  const onClickStopTimer = () => {
    dispatch(pauseTimer(timer?.id || ""));
    setIsOpenAddTimer(true);
  };

  const onClickTimer = () => {
    if (!timer?.workId) {
      return;
    }

    navigate(
      generateRoutePath([
        Pages.work.path,
        timer.workId,
        Pages.work.children.details.path,
      ]),
    );
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (timer?.timerStatus === "started") {
      interval = setInterval(() => {
        dispatch(increaseDuration());
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dispatch, timer?.timerStatus]);

  useEffect(() => {
    setStoredTimerState(timer, duration);
  }, [duration, timer]);

  const renderContent = () => {
    if (timer?.timerStatus === "started" || timer?.timerStatus === "paused") {
      return (
        <div
          className={
            "group relative inline-flex items-center gap-[10px] rounded-[9px] border border-[#E2DBFF] bg-[#F4F2FF] py-[5px] pl-3 pr-2"
          }
          data-contact-label={timerContactLabel}
          title={timerWorkLabel}>
          <button
            type={"button"}
            onClick={onClickTimer}
            disabled={!timer?.workId}
            className={
              "inline-flex cursor-pointer items-center gap-[7px] text-[#6B4EFF] disabled:cursor-default"
            }>
            <IoMdStopwatch className={"h-[15px] w-[15px]"} />
            <span
              className={
                "font-Manrope text-sm font-bold tracking-[0.3px] text-[#3A2E7A] [font-variant-numeric:tabular-nums]"
              }>
              {formatTime(duration, {
                isHours: true,
                isMinutes: true,
                isSeconds: true,
              })}
            </span>
          </button>
          <button
            type={"button"}
            aria-label={
              timer?.timerStatus === "paused" ? "Continue timer" : "Pause timer"
            }
            title={
              timer?.timerStatus === "paused" ? "Continue timer" : "Pause timer"
            }
            onClick={onClickPauseTimer}
            className={
              "inline-flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border border-[#E2DBFF] bg-white text-xs text-[#6B4EFF] transition-colors hover:bg-[#F8F6FF]"
            }>
            {timer?.timerStatus === "paused" ? (
              <FontAwesomeIcon icon={faPlay} />
            ) : (
              <FontAwesomeIcon icon={faPause} />
            )}
          </button>
          <button
            type={"button"}
            aria-label={"Stop timer"}
            title={"Stop timer"}
            onClick={onClickStopTimer}
            className={
              "inline-flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border border-[#FBD0CE] bg-white text-[11px] text-[#E5484D] transition-colors hover:bg-[#FFF5F5]"
            }>
            <FontAwesomeIcon icon={faStop} />
          </button>
          <span
            className={
              "pointer-events-none absolute right-0 top-[calc(100%+4px)] z-50 whitespace-nowrap rounded-lg bg-[#1F2433] px-3 py-2 text-left opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
            }>
            <span className={"block text-xs font-bold text-white"}>
              {timerWorkLabel}
            </span>
            <span
              className={"mt-1 block text-[11px] font-medium text-white/70"}>
              {timerContactLabel}
            </span>
          </span>
        </div>
      );
    } else {
      return (
        <Button
          iconOnly
          variant={"ghost"}
          leftIcon={<IoMdStopwatch className={"h-6 w-6"} />}
          onClick={() => setIsOpenStartTime(true)}
          className={"relative"}
        />
      );
    }
  };

  return (
    <>
      {renderContent()}
      {isOpenAddTimer && (
        <AddTimer isOpen={isOpenAddTimer} closeModal={closeAddTimer} />
      )}
      {isOpenStartTime && (
        <StartTimer
          isOpen={isOpenStartTime}
          closeModal={() => setIsOpenStartTime(false)}
        />
      )}
    </>
  );
};

export default Timer;
